import { useEffect, useMemo, useState } from "react";

import { CircularProgress, Grid, TextField as MUITextField } from "@mui/material";
import MuiAutocomplete, {
  type AutocompleteInputChangeReason,
} from "@mui/material/Autocomplete";
import match from "autosuggest-highlight/match";
import parse from "autosuggest-highlight/parse";
import { AxiosResponse } from "axios";
import {
  AutocompleteProps,
  AutocompleteRenderInputParams,
  fieldToAutocomplete,
  fieldToTextField,
} from "formik-mui";
import throttle from "lodash/throttle";
import queryString from "query-string";

import { axios } from "../api";

import type { MutableRefObject } from "react";
import type { AeronetV6ItemsResponse } from "../types";
import type { IJob } from "./types";
import { FormikAddJob } from "./FormikAddJob";
import { useConfig } from "../hooks/useConfig";
import { Quote } from "../quotes/types";
import { IAircraftSelect } from "../aircraft/types";

const STOCK_OPTION = { id: "0", name: "Stock" } as unknown as IJob;

export interface JobAutocompleteProps<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
> extends AutocompleteProps<T, Multiple, DisableClearable, FreeSolo> {
  label?: string;
  status: number[];
  quote?: Quote & { deliver: {id: string, name: string} };
  aircraft?: IAircraftSelect[];
  isAutoCreateJob?: boolean;
  isCreateJob?: boolean;
  handleAddDemand?: () => void;
  onChange?: (e,value) => void;
  createTypeId2nd?: string | number;
  createInitialDeliver?: {
    id: string | number;
    name: string;
  };
  onCreateDeliverChange?: (value: { id: string | number; name: string } | null) => void;
  showStockOption?: boolean;
  /** When true, omit the "Create New" option — select existing work orders only. */
  disableCreateNew?: boolean;
  /**
   * When true with deferJobCreationToParentSubmit false (legacy): choosing Create New
   * hides Add/Cancel and submits the nested job form immediately.
   * When deferJobCreationToParentSubmit is true: hides Add/Cancel; parent sheet calls
   * nestedFormRef.submitForm() on its own submit (e.g. Issue Part).
   */
  autoSubmitCreateJob?: boolean;
  deferJobCreationToParentSubmit?: boolean;
  nestedFormRef?: MutableRefObject<{
    submitForm: () => Promise<IJob>;
  } | null>;
  showWorkOrderType?: boolean;
}

export function JobAutocomplete<
  // eslint-disable-next-line
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
>(props: JobAutocompleteProps<IJob, Multiple, DisableClearable, FreeSolo>) {
  // @ts-ignore
  const {
    form,
    label = "Work Order Number",
    status,
    quote,
    isAutoCreateJob,
    isCreateJob,
    handleAddDemand,
    onChange,
    createTypeId2nd,
    createInitialDeliver,
    onCreateDeliverChange,
    showStockOption = true,
    disableCreateNew = false,
    autoSubmitCreateJob = false,
    deferJobCreationToParentSubmit = false,
    nestedFormRef,
    showWorkOrderType = false,
  } = props;
  const { setFieldValue } = form;
  const [jobs, setJobs] = useState<IJob[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [loading, setLoading] = useState(false);
  const { name } = fieldToTextField(props as any);
  const addLabel = "Create New";
  const jobFieldValue = form.values[name!];
  const isCreateNewSelection =
    typeof jobFieldValue === "string" && jobFieldValue.startsWith("Create");
  const mergedIsAutoCreateJob = Boolean(isAutoCreateJob || autoSubmitCreateJob);
  const mergedIsCreateJob = Boolean(
    isCreateJob ||
      (autoSubmitCreateJob &&
        isCreateNewSelection &&
        !deferJobCreationToParentSubmit)
  );
  const jobConfig = useConfig("jobs");
  const statusFilterKey = useMemo(
    () =>
      Array.isArray(status) && status.length > 0 ? status.join(",") : "",
    [status]
  );
  const fetchJobs = useMemo(() => {
    const statusInFilter: number[] =
      statusFilterKey === ""
        ? []
        : statusFilterKey
            .split(",")
            .map((part) => Number(part.trim()))
            .filter((n) => Number.isFinite(n));

    return throttle((value: string, callback: Function) => {
        let params: { [key: string]: any } = {
          page: 1,
          size: 20,
          sort: "-id",
          filter: [],
        };
        if (statusInFilter.length > 0) {
          params["filter"].push(
            JSON.stringify({
              field: "status",
              op: "in",
              value: statusInFilter,
            })
          );
        }
        if (value && value !== addLabel) {
          params["filter"].push(
            JSON.stringify({ field: "id", op: "ilike", value: value })
          );
          params["sort"] = "id";
        }
        setLoading(true);
        axios
          .get("/v6/jobs", {
            params: params,
            paramsSerializer: (params) => queryString.stringify(params),
            cache: false,
          })
          .then((resp: AxiosResponse<AeronetV6ItemsResponse<IJob>>) => {
            callback(resp.data.items);
          })
          .catch((error) => {
            callback([]);
          })
          .finally(() => {
            setLoading(false);
          });
      }, 200);
  }, [statusFilterKey]);

  useEffect(() => {
    let active = true;

    fetchJobs(inputValue, (results: ReadonlyArray<IJob>) => {
      if (active) {
        let newJobs: IJob[] = showStockOption ? [STOCK_OPTION] : [];

        if (results) {
          newJobs = [...newJobs, ...results.filter((job) => Number(job.id) !== 0)];
        }

        if (!disableCreateNew) {
          // @ts-ignore
          newJobs.unshift(addLabel);
        }

        setJobs(newJobs);
      }
    });

    return () => {
      active = false;
    };
  }, [inputValue, fetchJobs, addLabel, showStockOption, disableCreateNew]);

  return (
    <>
      <MuiAutocomplete
        {...fieldToAutocomplete(props)}
        fullWidth
        loading={loading}
        options={jobs}
        autoComplete
        includeInputInList
        onChange={(_event, newValue) => {
          setFieldValue("job", newValue);
          if (typeof newValue === "string") {
            setFieldValue("job_id", newValue);
          } else {
            // @ts-ignore
            setFieldValue("job_id", newValue && newValue.id ? newValue.id : "");
          }
          onChange && onChange(_event, newValue)
        }}
        onInputChange={(
          _event,
          newInputValue,
          reason: AutocompleteInputChangeReason
        ) => {
          // Only react to real typing / clear. "reset" fires when the parent form
          // re-renders or the value syncs — updating state then re-hit /v6/jobs on
          // every keystroke in unrelated fields.
          if (reason === "input" || reason === "clear") {
            setInputValue(newInputValue);
          }
        }}
        getOptionLabel={(option) => {
          if (typeof option === "string") {
            return option;
          } else {
            if (option.name) {
              return `${option.id!} - ${option.name}`;
            } else if (option.id) {
              return option.id!.toString();
            }
          }
          return "";
        }}
        isOptionEqualToValue={(option, value) => {
          const v = value as IJob | string | null | undefined;
          if (v == null || (typeof v === "string" && v === "")) {
            return option == null;
          }
          if (typeof v === "string" && typeof option === "string") {
            return v === option;
          }
          if (typeof v === "string" || typeof option === "string") {
            return false;
          }
          return option.id === (v as IJob).id;
        }}
        renderInput={(params: AutocompleteRenderInputParams) => (
          <MUITextField
            {...params}
            error={form.touched[name!] && !!form.errors[name!]}
            // @ts-ignore
            helperText={form.touched[name!] && form.errors[name!]}
            label={label}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option, state) => {
          if (typeof option === "string") {
            return (
              <li {...props} key={-1}>
                <Grid container alignItems="center">
                  <Grid item xs>
                    <span>{option}</span>
                  </Grid>
                </Grid>
              </li>
            );
          } else {
            const matches = match(option.id!, state.inputValue);
            const jobs = parse(option.id!, matches);

            return (
              <li {...props}>
                <Grid container alignItems="center">
                 
                  <Grid item xs>
                    {jobs.map((job, index) => (
                      <span
                        key={index}
                        style={{
                          fontWeight: job.highlight ? 700 : 400,
                        }}
                      >
                        {job.text}
                      </span>
                    ))}
                    {option.name ? " - " + option.name : null}
                  </Grid>
                </Grid>
              </li>
            );
          }
        }}
      />
      {
        !disableCreateNew &&
        typeof props.form.values[name!] === "string" &&
        props.form.values[name!].startsWith("Create") && (
          <FormikAddJob
            setValue={(newJob) => {
              if (newJob && typeof newJob !== "string") {
                setJobs((currentJobs) => {
                  const existingJobs = currentJobs.filter(
                    (job) => typeof job === "string" || job.id !== newJob.id
                  ) as IJob[];

                  return [addLabel as unknown as IJob, newJob, ...existingJobs];
                });
                setInputValue(String(newJob.id ?? ""));
              }

              setFieldValue(name!, newJob);
              setFieldValue('job_id', (newJob as IJob).id ?? '');
              setTimeout(() => {
                if (!deferJobCreationToParentSubmit) {
                  handleAddDemand?.();
                }
              });
            }}
            jobConfig={jobConfig}
            quote={quote}
            isAutoCreateJob={mergedIsAutoCreateJob}
            isCreateJob={mergedIsCreateJob}
            createTypeId2nd={createTypeId2nd}
            createInitialDeliver={createInitialDeliver}
            onDeliverChange={onCreateDeliverChange}
            nestedFormRef={nestedFormRef}
            showWorkOrderType={showWorkOrderType}
          />
        )}
    </>
  );
}

JobAutocomplete.displayName = "FormikMaterialUIJobAutocomplete";
